import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import i18n from '../i18/i18';
import { toast, Id, ToastOptions } from 'react-toastify';

const ReactSwal = withReactContent(Swal);

let currentToastId: Id | null = null;

const dismissCurrentToast = () => {
  if (currentToastId !== null) {
    toast.dismiss(currentToastId);
    currentToastId = null;
  }
};

const baseToastOptions: ToastOptions = {
  className: 'custom-toast',
  style: {
    fontWeight: 'bold',
    color: '#fff',
    zIndex: 11000
  }
};

const showSingleToast = (
  type: 'success' | 'error' | 'warning' | 'info',
  message: string,
  options?: ToastOptions
) => {
  if (currentToastId !== null) {
    toast.update(currentToastId, {
      render: message,
      type,
      isLoading: false,
      autoClose: 3000,
      ...options
    });
    return;
  }

  currentToastId = toast(message, {
    ...baseToastOptions,
    type,
    ...options,
    onClose: () => {
      currentToastId = null;
    }
  });
};

export const AlertService = {
  warning: async (
    title: string | JSX.Element,
    content: string | JSX.Element,
    preConfirm: () => any,
    confirmButtonText = i18n.t('general.continue')
  ) => {
    const { isConfirmed } = await ReactSwal.fire({
      title,
      html: content,
      icon: 'warning',
      iconColor: '#FFC700',
      confirmButtonText,
      cancelButtonText: i18n.t('general.btn-cancel'),
      customClass: {
        cancelButton: 'btn btn-secondary order-1 me-3',
        confirmButton: 'btn btn-primary order-2'
      },
      buttonsStyling: false,
      showCancelButton: true,
      showLoaderOnConfirm: true,
      preConfirm
    });

    return isConfirmed;
  },

  info: async (
    title: string | JSX.Element,
    content: string | JSX.Element,
    confirmButtonText = i18n.t('general.ok')
  ) => {
    const { isConfirmed } = await ReactSwal.fire({
      title,
      html: content,
      icon: 'info',
      iconColor: '#0070BC',
      confirmButtonText,
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false
    });

    return isConfirmed;
  },

  success: async (
    title: string | JSX.Element,
    content: string | JSX.Element,
    confirmButtonText = i18n.t('general.ok')
  ) => {
    const { isConfirmed } = await ReactSwal.fire({
      title,
      html: content,
      icon: 'success',
      iconColor: '#579743',
      confirmButtonText,
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false
    });

    return isConfirmed;
  },

  error: async (
    title: string | JSX.Element,
    content: string | JSX.Element,
    confirmButtonText = i18n.t('general.ok')
  ) => {
    const { isConfirmed } = await ReactSwal.fire({
      title,
      html: content,
      icon: 'error',
      iconColor: '#F1416C',
      confirmButtonText,
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false
    });

    return isConfirmed;
  },

  question: async (
    title: string | JSX.Element,
    content: string | JSX.Element,
    preConfirm: () => any,
    confirmButtonText = i18n.t('general.continue')
  ) => {
    const { isConfirmed } = await ReactSwal.fire({
      title,
      html: content,
      icon: 'question',
      iconColor: '#0070BC',
      confirmButtonText,
      cancelButtonText: i18n.t('general.btn-cancel'),
      customClass: {
        cancelButton: 'btn btn-secondary order-1 me-3',
        confirmButton: 'btn btn-primary order-2'
      },
      buttonsStyling: false,
      showCancelButton: true,
      showLoaderOnConfirm: true,
      preConfirm
    });

    return isConfirmed;
  },

  toastSuccess: (message: string) => {
    showSingleToast('success', message);
  },

  toastError: (message: string) => {
    showSingleToast('error', message);
  },

  toastWarning: (message: string) => {
    showSingleToast('warning', message);
  },

  toastInfo: (message: string) => {
    showSingleToast('info', message);
  },

  toastLoading: async <T>(
    promise: Promise<T>,
    loadingMessage: string,
    successMessage: string,
    errorMessage: string
  ): Promise<T> => {
    dismissCurrentToast();

    currentToastId = toast.loading(loadingMessage, {
      ...baseToastOptions,
      closeButton: false
    });

    try {
      const result = await promise;

      toast.update(currentToastId, {
        render: successMessage,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });

      return result;
    } catch (error) {
      toast.update(currentToastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });

      throw error;
    } finally {
      setTimeout(() => {
        currentToastId = null;
      }, 4000);
    }
  },

  toastLoadingWithProgress: (
    loadingMessage: string,
    successMessage: string,
    errorMessage: string
  ) => {
    dismissCurrentToast();

    const id = toast.loading(loadingMessage, {
      ...baseToastOptions,
      closeButton: false
    });
    currentToastId = id;

    const cleanup = () => {
      setTimeout(() => {
        if (currentToastId === id) currentToastId = null;
      }, 4000);
    };

    return {
      updateProgress: (percent: number) => {
        toast.update(id, { render: `${loadingMessage.replace(/\.\.\..*$/, '')}... ${percent}%` });
      },
      updateMessage: (message: string) => {
        toast.update(id, { render: message });
      },
      succeed: () => {
        toast.update(id, {
          render: successMessage,
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
        cleanup();
      },
      fail: () => {
        toast.update(id, {
          render: errorMessage,
          type: 'error',
          isLoading: false,
          autoClose: 4000
        });
        cleanup();
      }
    };
  }
};
